/// <reference types="vite/client" />
import { Node, NodeConfig } from '../types';
import { SHOULD_USE_MOCKS } from '../lib/clerk';

export const MOCK_NODE: Node = {
  id: 'node-1',
  slug: 'demo-org',
  name: 'Local Chapter',
  description: 'A sample organization for testing and demonstration.',
  parentId: 'parent-node-id',
  lineageIds: ['parent-node-id'],
  tier: 'LOCAL',
  storageUsedBytes: 52 * 1024 * 1024, // 52MB
  memberCount: 28,
  visibility: 'PUBLIC',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  config: {
    primaryColor: '#1E293B',
    secondaryColor: '#0F172A',
    logoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200"><rect width="200" height="200" fill="%230f172a" rx="40"/><path d="M100 40 L160 140 L40 140 Z" fill="%233b82f6"/><circle cx="100" cy="110" r="20" fill="%23ffffff"/></svg>',
    bannerUrl: 'https://picsum.photos/seed/banner/1200/400',
    industryCategory: 'Nonprofit',
    modules: ['CHAT', 'CALENDAR', 'LEDGER', 'REQUIREMENTS', 'GOALS', 'DIRECTORY', 'PULSE', 'DISCUSSIONS', 'ELECTIONS', 'NEWSLETTER', 'SUPPORT'],
    terminology: {
      member: 'Member',
      childNode: 'Chapter',
      parentNode: 'Council',
      pulse: 'Pulse',
      ledger: 'Ledger',
      requirements: 'Requirements',
      goals: 'Goals',
      discussions: 'Message Board',
      support: 'Help Desk'
    },
    features: {
      allowDirectUpstreamMessages: true,
      enforceParentTerminology: false
    },
    roleDefinitions: [
      { 
        name: 'PRESIDENT', 
        capabilities: ['create_announcement', 'send_email_blast', 'manage_members', 'view_ledger', 'configure_fees', 'verify_requirement', 'create_event', 'create_poll', 'manage_vault', 'create_newsletter', 'view_analytics', 'define_roles', 'create_sub_orgs', 'view_suborgs', 'edit_settings', 'manage_elections', 'cast_vote', 'view_support'],
        isLocked: true 
      },
      { 
        name: 'TREASURER', 
        capabilities: ['view_ledger', 'configure_fees', 'view_support'],
        isLocked: true 
      },
      { 
        name: 'COACH', 
        capabilities: ['create_event', 'create_announcement', 'view_support'],
        isLocked: false 
      },
      { 
        name: 'MODERATOR', 
        capabilities: ['create_announcement', 'create_event', 'create_poll', 'view_support'],
        isLocked: true 
      },
      {
        name: 'MEMBER',
        capabilities: [
          'cast_vote', 
          'view_newsletter', 
          'view_chat', 
          'view_calendar', 
          'view_requirements', 
          'view_goals', 
          'view_discussions', 
          'view_polls', 
          'view_directory', 
          'view_elections',
          'view_leadership',
          'view_personal_finances',
          'view_support'
        ],
        isLocked: true
      },
      {
        name: 'COORDINATOR',
        capabilities: ['create_event', 'create_announcement', 'manage_vault', 'view_support'],
        inherited: true
      }
    ]
  },
  modules: ['CHAT', 'CALENDAR', 'LEDGER', 'REQUIREMENTS', 'GOALS', 'DIRECTORY', 'PULSE', 'DISCUSSIONS', 'ELECTIONS', 'NEWSLETTER', 'SUPPORT', 'VAULT']
};

export const THEMES: Record<string, any> = {
  bauhaus: {
    primaryColor: '#0044FF', // Cobalt Blue
    secondaryColor: '#FFD600', // Yellow
    fontFamily: 'sans',
    roundedCorners: 'none',
  }
};

export const BRANDS: Record<string, { name: string; defaultThemeId: 'bauhaus'; config: Partial<NodeConfig> }> = {
  'orgsets.com': {
    name: 'OrgSets',
    defaultThemeId: 'bauhaus',
    config: {
      logoUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200"><rect width="200" height="200" fill="%23000000"/><path d="M 40 40 L 160 40 L 160 80 L 80 80 L 80 160 L 40 160 Z" fill="%23FFFFFF"/><rect x="100" y="100" width="60" height="60" fill="%230044FF"/><rect x="60" y="120" width="60" height="20" fill="%23FFD600"/><rect x="120" y="20" width="40" height="40" fill="%23FF2222"/></svg>',
      modules: ['CHAT', 'CALENDAR', 'LEDGER', 'REQUIREMENTS', 'GOALS', 'DIRECTORY', 'PULSE', 'DISCUSSIONS', 'ELECTIONS', 'NEWSLETTER', 'VAULT', 'POLLS', 'SUPPORT'],
      marketingCopy: {
        heroHeadline: 'RUN YOUR ENTIRE NETWORK—',
        heroSubheadline: 'FROM NATIONAL HQ TO LOCAL CHAPTERS.',
        heroDescription: 'Replace Mailchimp, Venmo, Google Drive, and group chats with one command center. Multi-chapter rosters, automated dues splitting, and instant SMS/email alerts.',
        heroCta: 'CREATE FREE ORGANIZATION',
        featuresHeadline: 'LESS ADMIN. MORE ACTION.',
        featuresSubheadline: 'From day one, you’ll have everything you need to lead your community with authority. Audit logs, elections, and structured leadership roles.'
      }
    }
  }
};

export const getBrandedNode = (domain?: string, slugOrId?: string): Node => {
  const selectedBrand = (domain && BRANDS[domain]) || BRANDS['orgsets.com'];
  const shouldAllowOverrides = SHOULD_USE_MOCKS || (import.meta as any).env?.VITE_ENABLE_DEVTOOLS === 'true';
  const forcedThemeId = shouldAllowOverrides && typeof localStorage !== 'undefined' ? localStorage.getItem('mock_theme_id') : null;
  const themeId = (forcedThemeId || selectedBrand.defaultThemeId) as 'bauhaus';
  const themeConfig = THEMES[themeId] || THEMES.bauhaus;
  
  const baseNode: Node = {
    ...MOCK_NODE,
    name: slugOrId === 'global' ? selectedBrand.name : MOCK_NODE.name,
    slug: slugOrId || 'global',
    visibility: 'PUBLIC',
    config: {
      ...MOCK_NODE.config,
      ...selectedBrand.config,
      ...themeConfig,
      themeId
    },
    modules: selectedBrand.config.modules || MOCK_NODE.modules
  };

  if (slugOrId && slugOrId !== 'global') {
    if (slugOrId === 'demo-org') {
      baseNode.name = 'Local Chapter';
    } else {
      baseNode.name = slugOrId.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
    }
  }

  return baseNode;
};
